import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import AddressLineField from './Fields/AddressLineField';
import StateField from './Fields/StateField';
import PhoneField from './Fields/PhoneField';
import EmailField from './Fields/EmailField';
import ZipCodeField from './Fields/ZipCodeField';
import NeighborhoodField from './Fields/NeighborhoodField';
import NicknameField from './Fields/NicknameField';
import NameField from './Fields/NameField';
import CountryField from './Fields/CountryField';
import CityField from './Fields/CityField';
import { DEFAULT_ADDRESS_LIMIT } from './constants';
import { isContrySupportedZipCode } from '../../address/utils';

const AddressFormFields = ({
  countryData = [],
  addressLimit = DEFAULT_ADDRESS_LIMIT,
  isAddressNameEditable = false,
  onEmailChange,
  requiredFields,
  selectedCountry,
  showAddressLine2 = false,
  showAddressName = false,
  showEmail = false,
  showName = false,
  showPhoneNumber = false,
  showPhoneNumberWithVerification = false,
  forcePhoneRequired = false,
  forceLine1Required = false,
  forceEmailRequired = false,
  isDeepLinkFlow = false,
  showNeighborhood = true,
  handleValueFromSessionStorage = false,
  valuefromSessionStorage = {},
  autoCompleteOnZipcode = false,
  withDropdownSearch = false
}) => {
  const countriesHaveNoZipOrState = useSelector(
    state => state?.dealer?.countriesHaveNoZipOrState
  );

  const [isZipCodeSupported, setIsZipCodeSupported] = useState(
    isContrySupportedZipCode(
      selectedCountry,
      countryData,
      countriesHaveNoZipOrState
    )
  );

  const isFieldRequired = fieldName => {
    let isRequiredField = requiredFields && requiredFields.includes(fieldName);
    if (fieldName === 'zipCode' && !isZipCodeSupported) {
      isRequiredField = false;
    }
    return isRequiredField;
  };

  const isAddress2Required = isFieldRequired('address2');

  const isAddressRequiredFlag =
    isFieldRequired('address1') || forceLine1Required;

  const isEmailRequiredFlag = isFieldRequired('email1') || forceEmailRequired;
  const isPhoneRequiredFlag = isFieldRequired('phone1') || forcePhoneRequired;

  return (
    <div className="d-flex flex-column gap-3">
      <NicknameField
        showAddressName={showAddressName}
        isAddressNameEditable={isAddressNameEditable}
      />
      <NameField showName={showName} />
      <CountryField
        countries={countryData}
        setIsZipCodeSupported={setIsZipCodeSupported}
      />
      <NeighborhoodField showNeighborhood={showNeighborhood} />
      <ZipCodeField
        isZipCodeRequired={isFieldRequired('zipCode')}
        autoCompleteOnZipCode={autoCompleteOnZipcode}
      />
      <AddressLineField
        isAddress2Required={isAddress2Required}
        showAddressLine2={showAddressLine2}
        isAddressRequiredFlag={isAddressRequiredFlag}
        addressLimit={addressLimit}
      />
      <CityField isCityRequired={isFieldRequired('city')} />
      <StateField
        isStateRequired={isFieldRequired('state')}
        countries={countryData}
        withSearchbox={withDropdownSearch}
      />
      <PhoneField
        showPhoneNumber={showPhoneNumber}
        showVerification={showPhoneNumberWithVerification}
        isPhoneRequiredFlag={isPhoneRequiredFlag}
        handleValueFromSessionStorage={handleValueFromSessionStorage}
        valuefromSessionStorage={valuefromSessionStorage}
        selectedCountry={selectedCountry}
      />
      <EmailField
        showEmail={showEmail}
        isEmailRequiredFlag={isEmailRequiredFlag}
        onEmailChange={onEmailChange}
        isDeepLinkFlow={isDeepLinkFlow}
      />
    </div>
  );
};

AddressFormFields.propTypes = {
  addressLimit: PropTypes.string,
  countryData: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      displayName: PropTypes.string.isRequired,
      states: PropTypes.arrayOf(
        PropTypes.shape({
          code: PropTypes.string,
          displayName: PropTypes.string
        })
      )
    })
  ),
  handleValueFromSessionStorage: PropTypes.bool,
  isAddressNameEditable: PropTypes.bool,
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  showAddressLine2: PropTypes.bool,
  showAddressName: PropTypes.bool,
  showEmail: PropTypes.bool,
  showName: PropTypes.bool,
  showPhoneNumber: PropTypes.bool,
  showPhoneNumberWithVerification: PropTypes.bool,
  onEmailChange: PropTypes.func,
  selectedCountry: PropTypes.string,
  forcePhoneRequired: PropTypes.bool,
  forceLine1Required: PropTypes.bool,
  forceEmailRequired: PropTypes.bool,
  isDeepLinkFlow: PropTypes.bool,
  showNeighborhood: PropTypes.bool,
  valuefromSessionStorage: PropTypes.shape({}),
  autoCompleteOnZipcode: PropTypes.bool,
  withDropdownSearch: PropTypes.bool
};

export default AddressFormFields;
